import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import styles from "./Bitiruvchilar.module.css";
import activityStyles from "./activityCards.module.css";
import classes from "./Activities.module.css";
import { testUrl } from "../BaseData";
import Pagination from "@mui/material/Pagination";

const ALUMNI_PER_PAGE = 3;

// Small line icons (inherit color via currentColor). Decorative -> aria-hidden.
const iconProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
    focusable: false,
};
const IconLocation = () => (
    <svg {...iconProps}>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);
const IconEducation = () => (
    <svg {...iconProps}>
        <path d="M22 10 12 5 2 10l10 5 10-5z" />
        <path d="M6 12v5c0 1.5 2.7 3 6 3s6-1.5 6-3v-5" />
    </svg>
);
const IconWork = () => (
    <svg {...iconProps}>
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
);
const IconSearch = () => (
    <svg {...iconProps}>
        <circle cx="11" cy="11" r="7" />
        <path d="M21 21l-4.3-4.3" />
    </svg>
);
const IconUser = () => (
    <svg {...iconProps}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);
const IconAward = () => (
    <svg {...iconProps}>
        <circle cx="12" cy="8" r="6" />
        <path d="M15.5 12.5 17 22l-5-3-5 3 1.5-9.5" />
    </svg>
);

const Bitiruvchilar = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    // Activities state
    const [activitiesLoading, setActivitiesLoading] = useState(true);
    const [activities, setActivities] = useState([]);
    const [pageApi, setPageApi] = useState(1);
    const activityItemsPerPage = 9;

    // Alumni state
    const [alumniLoading, setAlumniLoading] = useState(true);
    const [alumni, setAlumni] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [alumniPage, setAlumniPage] = useState(1);

    // Fetch activities
    useEffect(() => {
        setActivitiesLoading(true);
        axios.get(testUrl + "/api/activitiestype/")
            .then((res) => {
                const types = res.data;
                const targetType = types.find(type =>
                    (type.name_uz && type.name_uz.toLowerCase().includes("bitiruvchi")) ||
                    (type.name_ru && type.name_ru.toLowerCase().includes("bitiruvchi")) ||
                    (type.name_en && type.name_en.toLowerCase().includes("bitiruvchi")) ||
                    type.name_uz === "Bizning bitiruvchilar"
                );
                const shortName = targetType && targetType.short_name ? targetType.short_name : "bizning-bitiruvchilar";
                return axios.get(testUrl + `/api/activities/${shortName}/`);
            })
            .then((response) => {
                if (response && response.data) {
                    setActivities(response.data);
                }
            })
            .catch((err) => console.error("Error fetching activities:", err))
            .finally(() => setActivitiesLoading(false));
    }, [i18n.language]);

    // Fetch alumni
    useEffect(() => {
        setAlumniLoading(true);
        axios.get(testUrl + "/api/alumni/")
            .then((res) => {
                const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setAlumni(data);
            })
            .catch((err) => console.error("Error fetching alumni:", err))
            .finally(() => setAlumniLoading(false));
    }, [i18n.language]);

    // Filter alumni
    const filteredAlumni = useMemo(() => {
        let result = alumni;
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(a =>
                (a.full_name && a.full_name.toLowerCase().includes(q)) ||
                (a.name_uz && a.name_uz.toLowerCase().includes(q)) ||
                (a.surname_uz && a.surname_uz.toLowerCase().includes(q)) ||
                (a.faculty && a.faculty.toLowerCase().includes(q)) ||
                (a.education_field && a.education_field.toLowerCase().includes(q)) ||
                (a.workplace && a.workplace.toLowerCase().includes(q)) ||
                (a.address && a.address.toLowerCase().includes(q))
            );
        }
        return result;
    }, [alumni, searchQuery]);

    useEffect(() => { setAlumniPage(1); }, [searchQuery]);

    const totalAlumniPages = Math.ceil(filteredAlumni.length / ALUMNI_PER_PAGE);
    const paginatedAlumni = filteredAlumni.slice(
        (alumniPage - 1) * ALUMNI_PER_PAGE,
        alumniPage * ALUMNI_PER_PAGE
    );

    // Activities pagination
    const currentActivities = activities.slice(
        (pageApi - 1) * activityItemsPerPage,
        pageApi * activityItemsPerPage
    );

    const getDisplayName = (a) => {
        if (a.full_name) return a.full_name;
        return `${a.surname_uz || ''} ${a.name_uz || ''}`.trim() || "Noma'lum";
    };

    // First 1-2 letters of the name for the no-photo monogram (Latin/Cyrillic)
    const getInitials = (a) => {
        const name = (a.full_name || `${a.surname_uz || ''} ${a.name_uz || ''}`).trim();
        const letters = name.split(/\s+/)
            .map((p) => p[0])
            .filter((ch) => ch && /\p{L}/u.test(ch));
        return letters.slice(0, 2).join('').toUpperCase();
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        // birth_date is a "YYYY-MM-DD" string; read the year directly to avoid
        // new Date() UTC/local timezone shifts on Jan-1 dates.
        const match = String(dateStr).match(/(\d{4})/);
        return match ? match[1] : dateStr;
    };

    // Ta'lim yo'nalishi. Excel'da "Ta'lim yo'nalishi" ustuni ko'pincha bo'sh
    // qoladi, yo'nalish nomi ("Filologiya va tarix") esa "Fakultet" ustunida
    // keladi — shu sabab education_field bo'sh bo'lsa faculty'ga tushamiz.
    // Eski importlarda faculty'ga JSHIR (raqamlar) tushib qolgan — uni chiqarmaymiz.
    const getEducationField = (a) => {
        const edu = a.education_field && String(a.education_field).trim();
        if (edu) return edu;
        const fac = a.faculty && String(a.faculty).trim();
        if (fac && !/^\d{9,}$/.test(fac)) return fac;
        return '';
    };

    return (
        <div className={styles.pageWrapper}>
            {/* ===== BITIRUVCHILARIMIZ SECTION ===== */}
            <section className={styles.alumniSection}>
                {/* Section header */}
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionHeaderLeft}>
                        <h1 className={styles.sectionTitle}>BITIRUVCHILARIMIZ</h1>
                    </div>
                    <div className={styles.sectionHeaderRight}>
                        <div className={styles.searchWrapper}>
                            <span className={styles.searchIcon}><IconSearch /></span>
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder={t("Qidirish...")}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* Alumni cards grid */}
                {alumniLoading ? (
                    <div className={styles.loadingState}>
                        <div className={styles.spinner}></div>
                        {t("Yuklanmoqda...")}
                    </div>
                ) : paginatedAlumni.length > 0 ? (
                    <>
                        <div className={styles.alumniGrid}>
                            {paginatedAlumni.map((item) => {
                              const educationField = getEducationField(item);
                              // Har bir yozuvga "lavozimida ishlaydi" qo'shamiz. Faqat matn
                              // allaqachon "lavozim" so'zini o'z ichiga olsa (ya'ni "...lavozimida
                              // ishlaydi" allaqachon bor bo'lsa) qo'shmaymiz — takror bo'lmasin.
                              const rawWorkplace = String(item.workplace || '').trim();
                              const rawPosition = String(item.position || '').trim();
                              const employmentText = `${rawWorkplace} ${rawPosition}`.toLowerCase();
                              const appendLavozim = !!employmentText && !/lavozim/.test(employmentText);
                              // Suffix qo'shsak, oxirgi segmentdagi nuqtani olib tashlaymiz
                              // ("...tarbiyachi." -> "...tarbiyachi lavozimida ishlaydi.")
                              let wpText = rawWorkplace;
                              let posText = rawPosition;
                              if (appendLavozim) {
                                  if (posText) posText = posText.replace(/[.!?…\s]+$/, '');
                                  else wpText = wpText.replace(/[.!?…\s]+$/, '');
                              }
                              // Suffix qo'shmasak, matn nuqta bilan tugamagan bo'lsa nuqta qo'yamiz
                              const employmentEndsWithPunct = /[.!?…:]$/.test(rawPosition || rawWorkplace);
                              return (
                                <div key={item.id} className={styles.alumniCard}>
                                    <div className={styles.cardBody}>
                                        {/* Photo + achievements below it */}
                                        <div className={styles.cardPhotoSection}>
                                            <div className={styles.cardPhotoFrame}>
                                                {item.image ? (
                                                    <img src={item.image} alt={getDisplayName(item)} className={styles.cardPhoto} />
                                                ) : (
                                                    <div className={styles.cardPhotoPlaceholder} aria-hidden="true">
                                                        {getInitials(item) || <IconUser />}
                                                    </div>
                                                )}
                                            </div>

                                            {item.achievements && String(item.achievements).trim() && (
                                                <div className={styles.achievementsBox}>
                                                    <div className={styles.achievementsHeader}>
                                                        <span className={styles.achievementsIcon}><IconAward /></span>
                                                        Yutuqlari
                                                    </div>
                                                    <p className={styles.achievementsText}>
                                                        {String(item.achievements).trim()}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Info */}
                                        <div className={styles.cardInfoSection}>
                                            <p className={styles.infoName}>{getDisplayName(item)}</p>

                                            {(item.address || item.birth_date) && (
                                                <div className={styles.infoRow}>
                                                    <span className={styles.infoIcon}><IconLocation /></span>
                                                    <p className={styles.infoText}>
                                                        {item.address && <>{item.address}{item.birth_date ? 'da' : '.'}</>}
                                                        {item.address && item.birth_date && <br />}
                                                        {item.birth_date && <>{formatDate(item.birth_date)} yilda tug'ilgan.</>}
                                                    </p>
                                                </div>
                                            )}

                                            {(item.graduation_year || educationField) && (
                                                <div className={styles.infoRow}>
                                                    <span className={styles.infoIcon}><IconEducation /></span>
                                                    <p className={styles.infoText}>
                                                        {item.graduation_year && `${item.graduation_year} yilda`}
                                                        {educationField ? (
                                                            <>
                                                                {item.graduation_year && <br />}
                                                                <span className={styles.infoHighlight}>{educationField}</span>
                                                                <br />
                                                                ta'lim yo'nalishini tamomlagan.
                                                            </>
                                                        ) : (
                                                            <> ta'lim yo'nalishini tamomlagan.</>
                                                        )}
                                                    </p>
                                                </div>
                                            )}

                                            {(item.workplace || item.position) && (
                                                <div className={styles.infoRow}>
                                                    <span className={styles.infoIcon}><IconWork /></span>
                                                    <p className={styles.infoText}>
                                                        Hozirgi vaqtda
                                                        {wpText && <> {wpText}{posText ? 'da' : ''}</>}
                                                        {posText && <>{wpText ? <br /> : ' '}{posText}</>}
                                                        {appendLavozim ? <>{' '}lavozimida ishlaydi.</> : (employmentEndsWithPunct ? '' : '.')}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                              );
                            })}
                        </div>

                        {totalAlumniPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', margin: '30px 0' }}>
                                <Pagination
                                    onChange={(e, value) => setAlumniPage(value)}
                                    count={totalAlumniPages}
                                    page={alumniPage}
                                    variant="outlined"
                                    shape="rounded"
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyStateIcon}><IconEducation /></div>
                        <p className={styles.emptyStateText}>Bitiruvchilar topilmadi</p>
                        <p className={styles.emptyStateSubText}>
                            Ma'lumotlar admin paneldan Excel orqali yuklanadi
                        </p>
                    </div>
                )}

            </section>

            {/* ===== FAOLIYAT SECTION ===== */}
            <section className={styles.activitiesSection}>
                <div data-aos="fade-up" className={classes["container"]}>
                    <h1 className={classes["page-title"]}>{t("Faoliyat")}</h1>
                    {activitiesLoading ? (
                        <div className={styles.loadingState}>
                            <div className={styles.spinner}></div>
                            {t("Yuklanmoqda...")}
                        </div>
                    ) : (
                        <>
                            <div className={activityStyles.facultyGrid}>
                                {currentActivities.filter(Boolean).map((item) => (
                                    <div key={item.id} className={activityStyles.activityCard}
                                        onClick={() => navigate(`/activity/${item.id}`)}>
                                        <img src={item.image} alt=" " className={activityStyles.activityImage} />
                                        <div className={activityStyles.activityInfo}>
                                            <h3>{item[`name_${i18n.language}`] || item.name_uz}</h3>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {activities.length > activityItemsPerPage && (
                                <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0' }}>
                                    <Pagination
                                        onChange={(e, value) => setPageApi(value)}
                                        count={Math.ceil(activities.length / activityItemsPerPage)}
                                        page={pageApi}
                                        variant="outlined"
                                        shape="rounded"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Bitiruvchilar;
